import { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, type SubmitHandler, type UseFormSetValue, type UseFormWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Plus, Trash2 } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { TitleCaseInput, UpperInput, DigitsInput } from "@/components/ui/text-input";
import { LocationPicker } from "@/components/LocationPicker";
import {
  clienteCreateSchema,
  tipoClienteEnum,
  type ClienteCreateInput,
  type ClienteDetail,
  type ContactoInput,
} from "./schemas";
import { useCreateCliente, useUpdateCliente, syncContactos } from "./api";
import type { ApiError } from "@/lib/api";
import {
  applyPydanticErrors,
  flattenRhfErrors,
  labelForPath,
} from "@/lib/formErrors";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cliente?: ClienteDetail | null;
  onSaved?: (id: string) => void;
  /** rc145 — valores iniciales en modo crear (p.ej. desde "Convertir prospecto") */
  initialValues?: Partial<ClienteCreateInput>;
};

const DEFAULT_VALUES: ClienteCreateInput = {
  tipo: "residencial",
  empresa_id: 1,
  razon_social: "",
  rfc: "",
  nombre_comercial: "",
  curp: "",
  notas: "",
  notas_internas: "",
  tags: [],
  datos_fiscales: {
    regimen_fiscal: "",
    uso_cfdi_default: "",
    email_fiscal: "",
    calle: "",
    numero_exterior: "",
    numero_interior: "",
    colonia: "",
    codigo_postal: "",
    municipio: "",
    estado: "",
    pais: "MEX",
    referencias: "",
  },
  direccion_instalacion: {
    calle: "",
    numero_exterior: "",
    numero_interior: "",
    colonia: "",
    codigo_postal: "",
    municipio: "",
    estado: "",
    referencias: "",
  },
  contactos: [],
};

function toFormValues(c: ClienteDetail): ClienteCreateInput {
  const df = (c.datos_fiscales ?? {}) as Record<string, unknown>;
  const di = (c.direccion_instalacion ?? {}) as Record<string, unknown>;
  return {
    tipo: c.tipo,
    empresa_id: (c as unknown as { empresa_id?: number }).empresa_id ?? 1,
    razon_social: c.razon_social,
    rfc: c.rfc,
    nombre_comercial: c.nombre_comercial ?? "",
    curp: c.curp ?? "",
    notas: c.notas ?? "",
    notas_internas: c.notas_internas ?? "",
    tags: c.tags ?? [],
    datos_fiscales: {
      regimen_fiscal: (df.regimen_fiscal as string) ?? "",
      uso_cfdi_default: (df.uso_cfdi_default as string) ?? "",
      email_fiscal: (df.email_fiscal as string) ?? "",
      calle: (df.calle as string) ?? "",
      numero_exterior: (df.numero_exterior as string) ?? "",
      numero_interior: (df.numero_interior as string) ?? "",
      colonia: (df.colonia as string) ?? "",
      codigo_postal: (df.codigo_postal as string) ?? "",
      municipio: (df.municipio as string) ?? "",
      estado: (df.estado as string) ?? "",
      pais: ((df.pais as string) ?? "MEX"),
      referencias: (df.referencias as string) ?? "",
    },
    direccion_instalacion: {
      calle: (di.calle as string) ?? "",
      numero_exterior: (di.numero_exterior as string) ?? "",
      numero_interior: (di.numero_interior as string) ?? "",
      colonia: (di.colonia as string) ?? "",
      codigo_postal: (di.codigo_postal as string) ?? "",
      municipio: (di.municipio as string) ?? "",
      estado: (di.estado as string) ?? "",
      referencias: (di.referencias as string) ?? "",
    },
    contactos: c.contactos.map((x) => ({
      id: x.id, // importante para el diff en edit mode
      nombre: x.nombre,
      telefono: x.telefono ?? "",
      email: x.email ?? "",
      puesto: x.puesto ?? "",
      es_principal: x.es_principal,
      recibe_avisos: x.recibe_avisos,
      notas: x.notas ?? "",
    })),
  };
}

/** Stripea strings vacíos a undefined para que Pydantic no los vea como valores. */
function clean<T>(v: T): T {
  if (typeof v === "string") return (v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map(clean) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val);
      if (c !== undefined) out[k] = c;
    }
    return out as T;
  }
  return v;
}

/**
 * Subcomponente del mapa que sincroniza con los campos lat/long de RHF.
 * Usa `watch` para reactivar el pin cuando cambia externamente, y `setValue`
 * al click en el mapa. Evita el nested-Controller que rompía el submit.
 */
function ClienteLocationMap({
  watch,
  setValue,
}: {
  watch: UseFormWatch<ClienteCreateInput>;
  setValue: UseFormSetValue<ClienteCreateInput>;
}) {
  const lat = watch("direccion_instalacion.latitud");
  const lng = watch("direccion_instalacion.longitud");

  const value = useMemo(() => {
    const latNum = lat === undefined || lat === null || lat === "" ? null : Number(lat);
    const lngNum = lng === undefined || lng === null || lng === "" ? null : Number(lng);
    if (latNum === null || lngNum === null || isNaN(latNum) || isNaN(lngNum)) return null;
    return { lat: latNum, lng: lngNum };
  }, [lat, lng]);

  return (
    <LocationPicker
      value={value}
      onChange={(loc) => {
        setValue("direccion_instalacion.latitud", Number(loc.lat.toFixed(7)), {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("direccion_instalacion.longitud", Number(loc.lng.toFixed(7)), {
          shouldDirty: true,
          shouldValidate: true,
        });
      }}
    />
  );
}

export function ClienteFormDialog({ open, onOpenChange, cliente, onSaved, initialValues }: Props) {
  const [tab, setTab] = useState("generales");
  const [error, setError] = useState<string | null>(null);
  const editMode = !!cliente;
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError: setFieldError,
    formState: { errors, isSubmitted, isValid },
  } = useForm<ClienteCreateInput>({
    resolver: zodResolver(clienteCreateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { fields, append, remove } = useFieldArray({ control, name: "contactos" });

  useEffect(() => {
    if (open) {
      reset(
        cliente
          ? toFormValues(cliente)
          : {
              ...DEFAULT_VALUES,
              ...(initialValues ?? {}),
              direccion_instalacion: {
                ...DEFAULT_VALUES.direccion_instalacion,
                ...(initialValues?.direccion_instalacion ?? {}),
              },
              contactos: initialValues?.contactos ?? DEFAULT_VALUES.contactos,
            },
      );
      setTab("generales");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cliente, reset]);

  const createMut = useCreateCliente();
  const updateMut = useUpdateCliente(cliente?.id ?? "");

  const onSubmit: SubmitHandler<ClienteCreateInput> = async (values) => {
    setError(null);
    try {
      if (editMode && cliente) {
        // 1) PATCH cliente (sin contactos — backend no los acepta aquí)
        const { contactos, ...clienteFields } = values;
        await updateMut.mutateAsync(clean(clienteFields));

        // 2) Sync contactos por endpoints separados
        await syncContactos(
          cliente.id,
          cliente.contactos,
          contactos as ContactoInput[]
        );

        // Refrescar el detalle cacheado
        await qc.invalidateQueries({ queryKey: ["clientes"] });
        onSaved?.(cliente.id);
      } else {
        // Crear — los contactos van en el body del POST
        const cleaned = clean(values) as ClienteCreateInput;
        // Remover el `id` si se coló (no aplica en create)
        if (cleaned.contactos) {
          cleaned.contactos = cleaned.contactos.map(({ id: _drop, ...rest }) => rest) as ContactoInput[];
        }
        const saved = await createMut.mutateAsync(cleaned);
        onSaved?.(saved.id);
      }
      onOpenChange(false);
    } catch (e) {
      const api = e as ApiError;
      // Si el backend devuelve 422 Pydantic, mapear errores a los campos
      // específicos. El banner de arriba los lista legibles.
      const affected = applyPydanticErrors<ClienteCreateInput>(api, setFieldError);
      if (affected.length > 0) {
        setError(
          `Revisa los ${affected.length} campo(s) con error (marcados abajo).`
        );
      } else {
        setError(api.message ?? "No se pudo guardar el cliente");
      }
    }
  };

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          <DialogDescription>
            {editMode
              ? `Código: ${cliente!.codigo} — ${cliente!.razon_social}`
              : "Crea un cliente con sus datos fiscales, dirección y contactos."}
          </DialogDescription>
        </DialogHeader>

        <DialogContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="generales">Generales</TabsTrigger>
              <TabsTrigger value="fiscales">Fiscales</TabsTrigger>
              <TabsTrigger value="direccion">Dirección</TabsTrigger>
              <TabsTrigger value="contactos">
                Contactos {fields.length > 0 && `(${fields.length})`}
              </TabsTrigger>
            </TabsList>

            {/* === GENERALES === */}
            <TabsContent value="generales">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="tipo">
                    Tipo <span className="text-destructive">*</span>
                  </Label>
                  <Select id="tipo" {...register("tipo")}>
                    {tipoClienteEnum.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </Select>
                </div>
                <EmpresaSelect register={register} />
                <div>
                  <Label htmlFor="rfc">
                    RFC <span className="text-destructive">*</span>
                  </Label>
                  <UpperInput
                    id="rfc"
                    control={control}
                    name="rfc"
                    placeholder="XAXX010101000"
                    maxLength={13}
                  />
                  {errors.rfc && <span className="text-xs text-destructive">{errors.rfc.message}</span>}
                </div>
                <div className="col-span-2">
                  <Label htmlFor="razon_social">
                    Razón social <span className="text-destructive">*</span>
                  </Label>
                  <TitleCaseInput id="razon_social" control={control} name="razon_social" />
                  {errors.razon_social && (
                    <span className="text-xs text-destructive">{errors.razon_social.message}</span>
                  )}
                </div>
                <div className="col-span-2">
                  <Label htmlFor="nombre_comercial">Nombre comercial</Label>
                  <TitleCaseInput id="nombre_comercial" control={control} name="nombre_comercial" />
                </div>
                <div>
                  <Label htmlFor="curp">
                    CURP
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                      requerido para pagos Toku
                    </span>
                  </Label>
                  <UpperInput
                    id="curp"
                    control={control}
                    name="curp"
                    placeholder="AAAA######HDFXXX00"
                    maxLength={18}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Validado contra padrón RENAPO al sincronizar con Toku. Sin
                    CURP, la generación automática de CLABE/OXXO se pospone.
                  </p>
                  {errors.curp && <span className="text-xs text-destructive">{errors.curp.message}</span>}
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notas">Notas (visibles al cliente)</Label>
                  <Textarea id="notas" rows={2} {...register("notas")} />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notas_internas">Notas internas (privadas)</Label>
                  <Textarea id="notas_internas" rows={2} {...register("notas_internas")} />
                </div>
              </div>
            </TabsContent>

            {/* === FISCALES === */}
            <TabsContent value="fiscales">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="regimen">Régimen fiscal (clave SAT)</Label>
                  <Input id="regimen" {...register("datos_fiscales.regimen_fiscal")} placeholder="616" />
                </div>
                <div>
                  <Label htmlFor="uso_cfdi">Uso CFDI default</Label>
                  <Input id="uso_cfdi" {...register("datos_fiscales.uso_cfdi_default")} placeholder="G03" />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="email_fiscal">Email fiscal</Label>
                  <Input id="email_fiscal" type="email" {...register("datos_fiscales.email_fiscal")} />
                </div>
                <div className="col-span-2">
                  <Label>Calle + número</Label>
                  <div className="grid grid-cols-[1fr_100px_100px] gap-2">
                    <TitleCaseInput control={control} name="datos_fiscales.calle" placeholder="Calle" />
                    <Input placeholder="Ext." {...register("datos_fiscales.numero_exterior")} />
                    <Input placeholder="Int." {...register("datos_fiscales.numero_interior")} />
                  </div>
                </div>
                <div>
                  <Label>Colonia</Label>
                  <TitleCaseInput control={control} name="datos_fiscales.colonia" />
                </div>
                <div>
                  <Label>Código postal</Label>
                  <DigitsInput control={control} name="datos_fiscales.codigo_postal" maxLength={5} />
                </div>
                <div>
                  <Label>Municipio</Label>
                  <TitleCaseInput control={control} name="datos_fiscales.municipio" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <TitleCaseInput control={control} name="datos_fiscales.estado" />
                </div>
              </div>
            </TabsContent>

            {/* === DIRECCIÓN INSTALACIÓN === */}
            <TabsContent value="direccion">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="col-span-2">
                  <Label>Calle + número</Label>
                  <div className="grid grid-cols-[1fr_100px_100px] gap-2">
                    <TitleCaseInput control={control} name="direccion_instalacion.calle" placeholder="Calle" />
                    <Input placeholder="Ext." {...register("direccion_instalacion.numero_exterior")} />
                    <Input placeholder="Int." {...register("direccion_instalacion.numero_interior")} />
                  </div>
                </div>
                <div>
                  <Label>Colonia</Label>
                  <TitleCaseInput control={control} name="direccion_instalacion.colonia" />
                </div>
                <div>
                  <Label>Código postal</Label>
                  <DigitsInput control={control} name="direccion_instalacion.codigo_postal" maxLength={5} />
                </div>
                <div>
                  <Label>Municipio</Label>
                  <TitleCaseInput control={control} name="direccion_instalacion.municipio" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <TitleCaseInput control={control} name="direccion_instalacion.estado" />
                </div>
                <div className="col-span-2">
                  <Label>Referencias</Label>
                  <Textarea rows={2} {...register("direccion_instalacion.referencias")} />
                </div>
                <div className="col-span-2">
                  <Label>Ubicación exacta en mapa</Label>
                  <ClienteLocationMap watch={watch} setValue={setValue} />
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      step="0.0000001"
                      placeholder="Latitud"
                      {...register("direccion_instalacion.latitud", { valueAsNumber: false })}
                    />
                    <Input
                      type="number"
                      step="0.0000001"
                      placeholder="Longitud"
                      {...register("direccion_instalacion.longitud", { valueAsNumber: false })}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* === CONTACTOS === */}
            <TabsContent value="contactos">
              {fields.length === 0 && (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  Sin contactos registrados.
                </div>
              )}
              <div className="flex flex-col gap-3">
                {fields.map((field, i) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-[1fr_1fr_auto] gap-2 rounded-md border p-3"
                  >
                    <div>
                      <Label>Nombre <span className="text-destructive">*</span></Label>
                      <TitleCaseInput control={control} name={`contactos.${i}.nombre`} />
                      {errors.contactos?.[i]?.nombre && (
                        <span className="text-xs text-destructive">
                          {errors.contactos[i]?.nombre?.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Label>Puesto</Label>
                      <TitleCaseInput control={control} name={`contactos.${i}.puesto`} />
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="self-end rounded-md p-1 text-destructive hover:bg-destructive/10"
                      aria-label="Eliminar contacto"
                      title="Eliminar contacto"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div>
                      <Label>Teléfono</Label>
                      <Input {...register(`contactos.${i}.telefono`)} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" {...register(`contactos.${i}.email`)} />
                      {errors.contactos?.[i]?.email && (
                        <span className="text-xs text-destructive">
                          {errors.contactos[i]?.email?.message}
                        </span>
                      )}
                    </div>
                    <div />
                    <div className="col-span-3 flex items-center gap-4">
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          {...register(`contactos.${i}.es_principal`)}
                          className="h-3 w-3"
                        />
                        Principal
                      </label>
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          {...register(`contactos.${i}.recibe_avisos`)}
                          className="h-3 w-3"
                        />
                        Recibe avisos
                      </label>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    append({
                      nombre: "",
                      telefono: "",
                      email: "",
                      puesto: "",
                      es_principal: fields.length === 0,
                      recibe_avisos: true,
                      notas: "",
                    })
                  }
                >
                  <Plus size={12} /> Agregar contacto
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Banner de campos con error (fallback + resumen) */}
          {isSubmitted && !isValid && Object.keys(errors).length > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">Revisa estos campos:</span>
                <ul className="list-inside list-disc">
                  {flattenRhfErrors(errors as Record<string, unknown>)
                    .slice(0, 10)
                    .map(({ path, message }) => (
                      <li key={path}>
                        <span className="font-medium">{labelForPath(path)}</span>
                        {message && `: ${message}`}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting} size="sm">
            {submitting ? "Guardando…" : editMode ? "Guardar cambios" : "Crear cliente"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/**
 * rc19+: dropdown de Empresa.
 * Si solo hay UNA empresa configurada, lo renderiza solo para vista (deshabilitado)
 * para evitar pregunta innecesaria. Si hay 2+, lo muestra como selector real.
 */
type EmpresaOpt = { id: number; nombre: string; activa: boolean };
function EmpresaSelect({ register }: { register: ReturnType<typeof useForm<ClienteCreateInput>>["register"] }) {
  const { data: empresas = [], isLoading } = useQuery<EmpresaOpt[]>({
    queryKey: ["empresas", "for-cliente-form"],
    queryFn: () => apiCall<EmpresaOpt[]>("get", "/api/v1/empresas?only_activas=true"),
    staleTime: 60_000,
  });

  // Si solo hay 1 empresa, mostramos como info read-only para no marear al usuario
  const single = empresas.length === 1;

  return (
    <div>
      <Label htmlFor="empresa_id">
        Empresa {!single && <span className="text-destructive">*</span>}
      </Label>
      <Select
        id="empresa_id"
        disabled={isLoading || single}
        {...register("empresa_id", { valueAsNumber: true })}
      >
        {isLoading && <option value="1">Cargando…</option>}
        {!isLoading && empresas.length === 0 && <option value="1">Empresa 1 (default)</option>}
        {!isLoading && empresas.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nombre}
          </option>
        ))}
      </Select>
      {single && (
        <span className="text-[10px] text-muted-foreground">
          Solo hay una empresa configurada. Crea otra en Ajustes/Empresas para poder asignar.
        </span>
      )}
    </div>
  );
}
